import { i18n } from '@lingui/core';
import * as Sentry from '@sentry/react';

export const locales = {
  'en-US': 'English',
  'de-DE': 'Deutsch',
  'es-ES': 'Español',
  'fr-FR': 'Français',
  'nb-NO': 'Norsk',
  'ja-JP': '日本語',
};
export const defaultLocale = 'en-US';
const defaultLocaleObj = new Intl.Locale(defaultLocale);

/**
 * Tries to find the best matching locale we have translations for
 * based on the requested locale string
 *
 * Prioritization:
 * 1. Exact match of language, script, and region
 * 2. Match of language and script
 * 3. Match of language and region
 * 4. Match of language only
 * 5. Fallback to default locale
 */
export function getMatchingLocale(requestedLocaleString: string): Intl.Locale {
  try {
    const requestedLocale = new Intl.Locale(requestedLocaleString);
    const supportedLocales = Object.keys(locales).map(
      (l) => new Intl.Locale(l)
    );

    const matchingLanguages = supportedLocales.filter(
      (l) => l.language === requestedLocale.language
    );

    if (matchingLanguages.length === 0) {
      return defaultLocaleObj;
    }
    if (matchingLanguages.length === 1) {
      return matchingLanguages[0];
    }

    const matchingScript = matchingLanguages.filter(
      (l) => l.script === requestedLocale.script
    );

    if (matchingScript.length === 1) {
      return matchingScript[0];
    }

    if (matchingScript.length > 1) {
      const matchingRegion = matchingScript.find(
        (l) => l.region === requestedLocale.region
      );
      return matchingRegion || matchingScript[0];
    }

    const matchingRegion = matchingLanguages.filter(
      (l) => l.region === requestedLocale.region
    );

    if (matchingRegion.length === 1) {
      return matchingRegion[0];
    }

    return matchingLanguages[0];
  } catch (e) {
    console.warn(
      'Unable to parse or match locale, falling back to default locale'
    );
    return defaultLocaleObj;
  }
}

/**
 * We do a dynamic import of just the catalog that we need
 * @param locale any locale string
 */
export async function dynamicActivate(locale: string) {
  const matchedLocale = getMatchingLocale(locale);
  Sentry.addBreadcrumb({
    category: 'i18n',
    message: `Locale requested: "${locale}". Activating locale: "${matchedLocale.baseName}".`,
    level: 'info',
  });

  try {
    const catalog = await import(
      `./locales/${matchedLocale.baseName}/messages.po`
    );
    i18n.load(matchedLocale.baseName, catalog.messages);
    i18n.activate(matchedLocale.baseName);

    return matchedLocale.baseName;
  } catch (e) {
    Sentry.captureException(e, {
      tags: { 'rm.i18n_locale_load_failed': true },
    });

    // Retry once with the default locale rather than leaving i18n unactivated
    if (matchedLocale.baseName !== defaultLocale) {
      return dynamicActivate(defaultLocale);
    }

    throw e;
  }
}
